const makeTopic = (topic) => ({
  duration: '30-40 mins',
  xp: 100,
  difficulty: 'foundation',
  ...topic,
});

const worked = (label, content) => ({ label, content });

export const englishContent4 = {
  id: 4,
  title: 'Shakespeare',
  slug: 'shakespeare',
  color: 'purple',
  icon: 'Crown',
  description:
    "Shakespeare's plays, language, techniques, structure, performance, context, characters, themes and exam-style analysis.",
  topics: [

    // =========================================================
    // TOPIC 1 — BACKGROUND INFORMATION
    // =========================================================
    makeTopic({
      id: 'eng-4-1',
      slug: 'background-information',
      title: 'Background Information',
      summary: 'Who Shakespeare was, what he wrote and why his plays still matter.',

      explainer: {
        title: 'Background Information',
        content: `
William Shakespeare (1564–1616) is the most famous playwright in English literature. He wrote plays and poetry during the Elizabethan and Jacobean periods.

He wrote three main kinds of play:

- Tragedies — serious plays where ambition, hatred, jealousy or conflict often lead to death.
  Examples: "Macbeth", "Romeo and Juliet", "Hamlet", "Othello".

- Comedies — plays involving confusion, disguise, mistaken identity, love and reunion.
  Examples: "Twelfth Night", "A Midsummer Night's Dream", "Much Ado About Nothing".

- Histories — plays based on English kings and political conflict.
  Examples: "Henry V", "Richard III", "Henry IV".

Shakespeare's plays were performed in public theatres such as the Globe Theatre in London. Audiences included rich spectators seated in galleries and poorer spectators, called groundlings, who stood near the stage.

His plays were written to be performed, not just read. This means that voice, movement, costume, props, stage directions and audience reaction all matter.

When studying Shakespeare, remember three big ideas:
- The words matter.
- The stage matters.
- The original audience matters.
`
      },

      interactiveLesson: {
        title: 'Background',
        steps: [
          {
            type: 'question',
            question: 'Shakespeare lived mainly during:',
            options: ['The Elizabethan and Jacobean periods', 'The Victorian period'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A play that usually ends in death is most likely a:',
            options: ['Tragedy', 'Comedy'],
            correct: 0
          },
          {
            type: 'question',
            question: '"A Midsummer Night\'s Dream" is mainly a:',
            options: ['Comedy', 'History'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Romeo and Juliet" ends with the deaths of the two lovers.

Model answer:
This shows that the play is a tragedy. Shakespeare presents love as powerful but destructive because the lovers' deaths are caused by family conflict, secrecy and haste. The tragic ending leaves the audience with a sense of waste and sorrow.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Shakespeare wrote tragedies, comedies and histories. A tragedy usually ends sadly, often with deaths. "Romeo and Juliet" is a tragedy because both main characters die.`
        ),
        worked(
          'Developing',
          `Shakespeare uses tragedy to show how human weakness and social conflict can destroy lives. In "Romeo and Juliet", private love is crushed by public family hatred.`
        ),
        worked(
          'Grade 9 Ready',
          `Shakespeare's tragedies often move from disorder to catastrophe. In "Romeo and Juliet", the lovers' deaths expose the destructive nature of inherited hatred, forcing the older generation to recognise the consequences of their feud.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The Globe Theatre is associated with:',
          options: ['Shakespeare', 'Chaucer', 'George Orwell'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Shakespeare only wrote tragedies.',
          correct: false
        },
        {
          type: 'true-false',
          question: 'His history plays are based on English kings and political events.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Groundlings were:',
          options: ['Audience members who stood near the stage', 'Actors who played kings'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Shakespeare’s plays were written to be performed.',
          correct: true
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Explain one key difference between a Shakespearean tragedy and a comedy. Give one example of each.`,
        sentenceStarter: 'A tragedy is different from a comedy because...',
        hints: [
          'Mention how each type of play usually ends.',
          'Name one tragedy and one comedy.',
          'Try to explain the audience reaction.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Explains tragedy correctly',
          'Explains comedy correctly',
          'Names at least one play of each type',
          'Comments on audience reaction'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why do you think Shakespeare's plays are still studied and performed more than 400 years later?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 2 — SHAKESPEARE'S LANGUAGE
    // =========================================================
    makeTopic({
      id: 'eng-4-2',
      slug: 'shakespeares-language',
      title: "Shakespeare's Language",
      summary: 'Understand older words, contractions, pronouns and unfamiliar phrasing.',

      explainer: {
        title: "Shakespeare's Language",
        content: `
Shakespeare's English is over 400 years old, so some words and sentence patterns look unfamiliar. The trick is not to panic. Most lines become much easier once you recognise common patterns.

1. Missing letters

Shakespeare often uses apostrophes to show missing letters:

- "'tis" = it is
- "o'er" = over
- "e'en" = even
- "ne'er" = never
- "i'" = in

2. Older pronouns

- "thou" = you, usually singular and familiar
- "thee" = you, when receiving an action
- "thy" = your
- "thine" = yours, or your before a vowel sound
- "ye" = you, often plural or formal

3. Common older words

- "hark" = listen
- "wherefore" = why
- "anon" = soon
- "aye" = yes
- "nay" = no
- "hence" = from here / away
- "doth" = does
- "hath" = has
- "art" = are

4. Meaning before analysis

Before analysing a Shakespeare line, first translate it into plain modern English. Then look at the method and effect.

Example:
"Wherefore art thou Romeo?"

This does not mean "Where are you, Romeo?"
It means "Why are you Romeo?" Juliet is asking why Romeo has to belong to the enemy family.
`
      },

      interactiveLesson: {
        title: 'Shakespearean Words',
        steps: [
          {
            type: 'question',
            question: '"Wherefore art thou Romeo?" means:',
            options: ['Why are you Romeo?', 'Where are you, Romeo?'],
            correct: 0
          },
          {
            type: 'question',
            question: '"\'Tis" means:',
            options: ['It is', 'This is'],
            correct: 0
          },
          {
            type: 'question',
            question: '"Thy" means:',
            options: ['Your', 'Their'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Hark! What light through yonder window breaks?"

Modern meaning:
"Listen! What light is coming through that window over there?"

Model answer:
The word "Hark" creates a dramatic pause, as if Romeo is suddenly struck by what he sees. The image of "light" presents Juliet as bright, beautiful and almost heavenly. Shakespeare uses this moment to show Romeo's intense idealisation of Juliet.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `"Hark" means "listen". Romeo is telling himself or the audience to pay attention because he has noticed something important.`
        ),
        worked(
          'Developing',
          `The image of "light" suggests Juliet seems beautiful and hopeful to Romeo. It makes her appear different from the darkness around her.`
        ),
        worked(
          'Grade 9 Ready',
          `Shakespeare turns Juliet into a source of "light", suggesting that Romeo sees her not simply as a person but as a transforming presence. This idealisation is romantic, but it may also suggest how quickly Romeo exaggerates his feelings.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Ne\'er" means:',
          options: ['Never', 'Near', 'Now'],
          correct: 0
        },
        {
          type: 'mcq',
          question: '"Thee" is:',
          options: ['An object form of "you"', 'The word "the"', 'A type of stage direction'],
          correct: 0
        },
        {
          type: 'true-false',
          question: '"Hark" means "look".',
          correct: false
        },
        {
          type: 'mcq',
          question: '"Hath" means:',
          options: ['Has', 'Had', 'Hate'],
          correct: 0
        },
        {
          type: 'true-false',
          question: '"Wherefore" usually means "why".',
          correct: true
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite this line in modern English: "Thou hast spoken truly, but anon I must away."`,
        sentenceStarter: 'You have...',
        hints: [
          '"Thou hast" = you have.',
          '"Anon" = soon.',
          '"Away" means leave or go.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Correctly converts "thou hast"',
          'Correctly converts "anon"',
          'Keeps the original meaning',
          'Uses clear modern English'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why might Shakespeare use intimate words like "thou" in one relationship but more formal language in another?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 3 — SHAKESPEARE'S SENTENCE STRUCTURE
    // =========================================================
    makeTopic({
      id: 'eng-4-3',
      slug: 'shakespeares-sentence-structure',
      title: "Shakespeare's Sentence Structure",
      summary: 'Prose, verse, iambic pentameter and unusual word order.',

      explainer: {
        title: "Shakespeare's Sentence Structure",
        content: `
Shakespeare's characters do not all speak in the same way. Their sentence structure can reveal status, emotion and control.

1. Prose

Prose is ordinary speech written in normal lines and paragraphs. It is often used by:
- Lower-status characters
- Comic characters
- Characters speaking casually
- Characters whose thoughts are confused or uncontrolled

2. Verse

Verse is speech arranged in lines, often with rhythm. Noble or important characters often speak in verse, especially in serious moments.

3. Iambic pentameter

Shakespeare often uses iambic pentameter:
- Five pairs of syllables
- Usually ten syllables in total
- Often unstressed then stressed

A rough rhythm is:
da-DUM da-DUM da-DUM da-DUM da-DUM

Example:
"But SOFT | what LIGHT | through YON | der WIN | dow BREAKS"

4. Broken rhythm

If a line is short, interrupted or rhythmically uneven, it can suggest:
- Shock
- Fear
- Grief
- Anger
- Loss of control

5. Unusual word order

Shakespeare sometimes changes normal word order for emphasis.

Modern order:
"The king is dead."

Shakespearean emphasis:
"Dead is the king."

The unusual order pushes the important word to the front and makes the line more dramatic.
`
      },

      interactiveLesson: {
        title: 'Prose vs Verse',
        steps: [
          {
            type: 'question',
            question: 'Iambic pentameter usually has how many syllables per line?',
            options: ['10', '14'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Prose is often used by:',
            options: ['Lower-status or comic characters', 'Only kings'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A broken or incomplete line can suggest:',
            options: ['Shock or emotional disturbance', 'Perfect calm'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"In fair Verona, where we lay our scene"

Model answer:
This line is written in controlled verse. Its balanced rhythm suits the formal prologue, where the audience is being introduced to the setting and conflict. The phrase "fair Verona" makes the city sound beautiful, which contrasts with the violence that will happen there.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `The line has a steady rhythm. This makes the opening sound controlled and formal.`
        ),
        worked(
          'Developing',
          `The phrase "fair Verona" presents the setting as beautiful, but this contrasts with the violence between the families.`
        ),
        worked(
          'Grade 9 Ready',
          `The formal verse gives the prologue authority, almost like a public announcement. By describing Verona as "fair", Shakespeare creates irony: the city may appear civilised, but it contains a destructive feud.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Iambic pentameter is a type of:',
          options: ['Metre', 'Rhyme scheme', 'Stage prop'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Noble characters often speak in verse.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'Shakespeare always uses modern English word order.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Prose is written:',
          options: ['Like ordinary speech', 'Only in rhyming couplets'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A change from verse to prose can reveal a change in mood or control.',
          correct: true
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Why might Shakespeare switch a noble character from verse into prose during a scene?`,
        sentenceStarter: 'Switching into prose could show...',
        hints: [
          'Think about emotion.',
          'Think about loss of control.',
          'Think about comedy or madness.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Links prose to emotion, comedy or disorder',
          'Shows awareness that verse can suggest status or control',
          'Explains the effect on the audience'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `How can rhythm reveal what a character is feeling before they directly say it?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 4 — SHAKESPEARE'S TECHNIQUES
    // =========================================================
    makeTopic({
      id: 'eng-4-4',
      slug: 'shakespeares-techniques',
      title: "Shakespeare's Techniques",
      summary: 'Imagery, puns, foreshadowing, oxymorons, rhetorical questions and soliloquies.',

      explainer: {
        title: "Shakespeare's Techniques",
        content: `
Shakespeare uses dramatic and literary techniques to create meaning.

Key techniques:

Imagery:
Vivid descriptive language that creates pictures in the audience's mind. Images of blood, light, darkness, disease, storms or animals often reveal themes.

Puns:
Wordplay using two meanings of a word. In "Romeo and Juliet", Mercutio jokes that he will be "a grave man" after he is stabbed. "Grave" means serious and also a burial place.

Foreshadowing:
A hint about what will happen later. In tragedy, foreshadowing creates dread because the audience senses disaster approaching.

Oxymoron:
Two contradictory words placed together, such as "loving hate" or "heavy lightness". Oxymorons are useful for showing emotional confusion.

Rhetorical question:
A question asked for effect, not because the speaker expects an answer. It often reveals inner conflict.

Soliloquy:
A speech where a character speaks their thoughts aloud, usually alone on stage. This lets the audience hear private fears, desires or guilt.

Dramatic irony:
When the audience knows something that one or more characters do not. This can create comedy, tension or tragedy.

To analyse a technique well, do not just name it. Explain what it reveals and how it affects the audience.
`
      },

      interactiveLesson: {
        title: 'Shakespearean Techniques',
        steps: [
          {
            type: 'question',
            question: '"A grave man" meaning both serious and in his grave is a:',
            options: ['Pun', 'Simile'],
            correct: 0
          },
          {
            type: 'question',
            question: '"Loving hate" is an example of:',
            options: ['An oxymoron', 'A rhyme'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A speech where a character reveals private thoughts alone is a:',
            options: ['Soliloquy', 'Stage direction'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
Macbeth says, "Stars, hide your fires; Let not light see my black and deep desires."

Model answer:
Shakespeare uses light and dark imagery to reveal Macbeth's hidden ambition. The noun "stars" suggests heaven, morality and judgement, while "black" suggests evil and secrecy. Macbeth wants to hide his "desires", showing that he already knows they are morally wrong. This foreshadows Duncan's murder and makes the audience fear Macbeth's ambition.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `The words "light" and "black" create a contrast. Macbeth wants to hide his bad thoughts.`
        ),
        worked(
          'Developing',
          `The image of "black and deep desires" suggests Macbeth's ambition is secret and dangerous. He knows his thoughts are wrong.`
        ),
        worked(
          'Grade 9 Ready',
          `Shakespeare externalises Macbeth's conscience through light and darkness. By asking the "stars" to hide their "fires", Macbeth imagines morality itself as something watching him, which makes his ambition feel both private and spiritually corrupt.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Fortune\'s fool" suggests:',
          options: ['Bad luck or fate controlling events', 'A harmless joke'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A pun uses two meanings of a word.',
          correct: true
        },
        {
          type: 'true-false',
          question: 'Shakespeare never uses rhetorical questions.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'A soliloquy helps the audience understand:',
          options: ['A character’s private thoughts', 'Only the setting'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'When the audience knows more than a character, this is:',
          options: ['Dramatic irony', 'Pathetic fallacy'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Explain how ONE Shakespearean technique adds depth to a line. Choose from imagery, pun, foreshadowing, oxymoron, rhetorical question, soliloquy or dramatic irony.`,
        sentenceStarter: 'The technique of...',
        hints: [
          'Name the technique.',
          'Use a brief example.',
          'Explain what the audience understands.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names a technique accurately',
          'Uses a brief example',
          'Explains what the technique reveals',
          'Comments on audience effect'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is it not enough just to spot a technique in Shakespeare? What must analysis do next?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 5 — STRUCTURE
    // =========================================================
    makeTopic({
      id: 'eng-4-5',
      slug: 'shakespeare-structure',
      title: 'Structure',
      summary: 'Openings, turning points, contrasts, tension and endings in Shakespeare.',

      explainer: {
        title: 'Structure',
        content: `
Structure means how a play is organised.

Shakespeare's plays often use a five-act structure:

- Act 1: Exposition — characters, setting and conflict are introduced.
- Act 2: Development — plans, tensions and relationships become more complicated.
- Act 3: Crisis or turning point — something happens that changes the direction of the play.
- Act 4: Consequences — problems deepen and characters face the results of earlier choices.
- Act 5: Resolution — order is restored, often through marriage in comedy or death in tragedy.

Important structural features:

Opening hook:
Many plays begin with action or mystery. "Macbeth" begins with witches and thunder. "Romeo and Juliet" begins with public conflict.

Turning point:
A moment that changes the direction of the play. In "Romeo and Juliet", Mercutio and Tybalt's deaths shift the play from romance towards tragedy.

Contrast:
Shakespeare often places scenes next to each other to create contrast, such as comedy before violence or love before death.

Rising tension:
Tension builds as secrets, mistakes or ambitions grow harder to control.

Ending:
The ending shows what has been restored, lost or learned.

When analysing structure, ask:
- Why does this moment happen here?
- What came before it?
- What changes after it?
`
      },

      interactiveLesson: {
        title: 'Five-Act Structure',
        steps: [
          {
            type: 'question',
            question: 'A Shakespeare play usually has how many acts?',
            options: ['5', '3'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A turning point is:',
            options: ['A moment that changes the direction of the play', 'A list of characters'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Tragedies usually end with:',
            options: ['Deaths and loss', 'Only weddings'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
In "Macbeth", Duncan is murdered early in the play.

Model answer:
Placing Duncan's murder early means the audience focuses less on whether Macbeth will commit the crime and more on the consequences of committing it. The rest of the play becomes a study of guilt, paranoia and tyranny. Structurally, Shakespeare turns Macbeth's ambition into a chain reaction.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Duncan's murder happens early, so the rest of the play shows what happens afterwards.`
        ),
        worked(
          'Developing',
          `The early murder makes the audience watch Macbeth's guilt and fear grow. This creates tension because his power becomes unstable.`
        ),
        worked(
          'Grade 9 Ready',
          `By placing regicide early, Shakespeare shifts the dramatic question from "Will Macbeth kill?" to "What does unlawful power do to the human mind and the state?" The structure turns one crime into a prolonged moral collapse.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'A dramatic opening is designed to:',
          options: ['Hook the audience', 'Delay the plot'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Comedies often end with marriages or reunions.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A turning point usually:',
          options: ['Changes what happens next', 'Repeats the opening exactly'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Where a moment appears in a play can affect its meaning.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Rising tension means:',
          options: ['Pressure increases as the plot develops', 'Nothing changes'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Why might a playwright place the most shocking event near the beginning of a play rather than at the end?`,
        sentenceStarter: 'Placing a shocking event early could...',
        hints: [
          'Think about audience attention.',
          'Think about consequences.',
          'Think about how tension develops afterwards.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Gives a clear structural reason',
          'Links placement to audience response',
          'Explains what the rest of the play can then explore'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `How does the idea of a "turning point" help you write about any Shakespeare play?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 6 — PERFORMANCE
    // =========================================================
    makeTopic({
      id: 'eng-4-6',
      slug: 'performance',
      title: 'Performance',
      summary: 'Dialogue, stagecraft, props, sound, costume, movement and interpretation.',

      explainer: {
        title: 'Performance',
        content: `
A Shakespeare play is written to be performed. The script is only part of the meaning.

Performance elements include:

Dialogue:
How actors speak the lines. Pace, pauses, volume and emphasis can completely change meaning.

Stage directions:
Instructions about action, entrances, exits, sounds or movement. Even short stage directions can be important.

Props:
Objects used onstage, such as a dagger, crown, letter, potion or sword.

Sound:
Thunder, music, drums, bells and offstage noises can create atmosphere or signal danger.

Costume:
Costume can reveal status, gender, power, disguise or period.

Lighting:
Modern productions use lighting to suggest mood, time, danger or the supernatural.

Movement and body language:
An actor's posture, distance from others, stillness, gestures and facial expression all affect meaning.

Interpretation:
The same scene can be staged in different ways. A line might be played as a joke, a threat, a confession or a breakdown.

When analysing Shakespeare, do not only ask:
"What does the line mean?"

Also ask:
"How might this look and sound on stage?"
`
      },

      interactiveLesson: {
        title: 'Performance',
        steps: [
          {
            type: 'question',
            question: 'Costumes in Shakespeare can signal:',
            options: ['Status, role or disguise', 'Nothing important'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A pause during delivery can suggest:',
            options: ['Hesitation or tension', 'That the line is meaningless'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Two directors staging the same scene may:',
            options: ['Interpret it differently', 'Have to make it identical'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
The witches first appear in "Macbeth" with "Thunder and lightning."

Model answer:
The stage direction creates a threatening atmosphere before any character speaks. The sound of thunder and the visual effect of lightning suggest disorder in nature. This prepares the audience to see the witches as unnatural and dangerous.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Thunder and lightning make the opening feel scary and dangerous.`
        ),
        worked(
          'Developing',
          `The storm suggests that nature itself is disturbed. This makes the witches seem powerful and unnatural.`
        ),
        worked(
          'Grade 9 Ready',
          `The violent weather externalises moral disorder before the plot has even begun. Shakespeare uses stagecraft to make the audience feel that the natural world is already unstable, preparing them for a tragedy driven by corruption and chaos.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is a prop?',
          options: ['A crown held by an actor', 'The rhyme scheme'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Every production of a Shakespeare play must look identical.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'A stage direction can show:',
          options: ['Action or sound on stage', 'Only the date the play was written'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Body language can change how an audience understands a line.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Interpretation means:',
          options: ['A chosen way of presenting meaning', 'Copying every production exactly'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Describe one performance choice for a tense Shakespeare scene and explain its effect on the audience.`,
        sentenceStarter: 'I would use...',
        hints: [
          'Choose costume, prop, sound, lighting, movement or voice.',
          'Explain what emotion it creates.',
          'Link it to the audience.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names a performance choice',
          'Links the choice to atmosphere or emotion',
          'Considers the audience',
          'Explains why it suits the scene'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why can the same Shakespeare line seem funny in one production but frightening in another?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 7 — CONTEXT
    // =========================================================
    makeTopic({
      id: 'eng-4-7',
      slug: 'shakespeare-context',
      title: 'Context',
      summary: 'Elizabethan and Jacobean beliefs, hierarchy, gender, religion and theatre.',

      explainer: {
        title: 'Context',
        content: `
Context means the world around the text: the beliefs, values, politics and social rules that shaped the writer and the original audience.

Key contextual ideas for Shakespeare:

Social hierarchy:
Society was unequal. Kings, queens and nobles had far more power than ordinary people. Many believed this order was part of God's design.

The Divine Right of Kings:
Many people believed monarchs were chosen by God. This makes the murder of a king in "Macbeth" much more shocking.

The supernatural:
Belief in witches, ghosts, spirits, omens and fate was widespread. Supernatural events on stage could feel serious and frightening to many in the original audience.

Gender and patriarchy:
Men held most public power. Women had fewer legal, educational and social freedoms than men. This makes strong female characters such as Lady Macbeth, Beatrice, Viola and Portia especially interesting.

Marriage and family:
Marriage was often linked to family duty, inheritance, status and obedience, not just love.

Theatre:
Theatre was popular entertainment. Audiences could be noisy and active. Shakespeare had to appeal to different social groups at once.

Using context well:
Do not bolt context onto the end of a paragraph. Link it directly to the question.

Weak:
"Women had fewer rights then."

Stronger:
"Lady Macbeth's dominance would unsettle a Jacobean audience because it challenges expectations of female obedience and male authority."
`
      },

      interactiveLesson: {
        title: "Shakespeare's World",
        steps: [
          {
            type: 'question',
            question: "In Shakespeare's time, many people believed kings were:",
            options: ['Chosen by God', 'Elected by voters'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Female roles on the public stage were usually played by:',
            options: ['Young male actors', 'Professional actresses'],
            correct: 0
          },
          {
            type: 'question',
            question: 'The cheapest audience members at the Globe were called:',
            options: ['Groundlings', 'Lords'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
In "Macbeth", the murder of King Duncan is followed by strange events in nature.

Model answer:
This reflects the belief that a king was divinely appointed. Killing Duncan is not only a political crime but an attack on God's order. The disruption in nature would suggest to a Jacobean audience that Macbeth has broken the moral structure of the world.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `People believed kings were chosen by God. This makes Duncan's murder seem even worse.`
        ),
        worked(
          'Developing',
          `The murder of Duncan shocks the audience because Macbeth does not just kill a man; he attacks the natural and religious order.`
        ),
        worked(
          'Grade 9 Ready',
          `For a Jacobean audience, regicide would represent cosmic disorder. Shakespeare uses unnatural events after Duncan's murder to dramatise the collapse of political, moral and spiritual order under Macbeth's ambition.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The groundlings in the Globe:',
          options: ['Stood near the stage', 'Sat on the throne'],
          correct: 0
        },
        {
          type: 'true-false',
          question: "Female roles were usually played by women in Shakespeare's public theatre.",
          correct: false
        },
        {
          type: 'mcq',
          question: 'The Divine Right of Kings means:',
          options: ['The monarch was believed to be chosen by God', 'Everyone voted for the monarch'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Context should be linked to the point being made.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Patriarchy means:',
          options: ['A society where men hold most power', 'A society ruled by children'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Explain how one piece of context can deepen your reading of a Shakespeare play.`,
        sentenceStarter: 'Knowing that...',
        hints: [
          'Choose one contextual idea.',
          'Link it to a character, event or theme.',
          'Avoid adding context that does not support your point.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Explains one contextual idea accurately',
          'Links it to a play',
          'Uses context to support analysis',
          'Avoids irrelevant historical detail'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is it risky to judge Shakespeare's characters only by modern values?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 8 — CHARACTERS
    // =========================================================
    makeTopic({
      id: 'eng-4-8',
      slug: 'shakespeare-characters',
      title: 'Characters',
      summary: 'Heroes, tragic flaws, villains, fools, rulers and strong female characters.',

      explainer: {
        title: 'Characters',
        content: `
Shakespeare created complex characters who often change across a play.

Common character types:

Romantic heroes:
Characters driven by love, passion or idealism. Romeo is an example.

Tragic heroes:
Important characters who are destroyed partly by their own weakness or error. Macbeth's ambition and Hamlet's hesitation are examples.

Villains:
Characters who manipulate, harm or deceive others. Iago in "Othello" is a famous example of a calculating villain.

Fools:
Comic characters who often speak truth through jokes. The fool can sometimes understand the truth more clearly than powerful characters.

Rulers and nobles:
Kings, queens, princes and lords whose decisions affect whole societies.

Strong female characters:
Characters such as Lady Macbeth, Beatrice, Viola and Portia challenge expectations in different ways. They may be witty, ambitious, resourceful or morally courageous.

Character development:
Shakespeare's characters often change. A confident character may become guilty. A comic character may reveal wisdom. A loving character may become violent or desperate.

Good character analysis asks:
- What does the character want?
- What weakness or conflict do they have?
- How do they change?
- How does Shakespeare make the audience respond to them?
`
      },

      interactiveLesson: {
        title: 'Character Types',
        steps: [
          {
            type: 'question',
            question: 'A hero destroyed partly by a weakness is a:',
            options: ['Tragic hero', 'Comic servant'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A fool in Shakespeare often:',
            options: ['Speaks truth through jokes', 'Cannot speak'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Lady Macbeth and Beatrice are examples of:',
            options: ['Strong female characters', 'Identical characters'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
Hamlet delays acting against Claudius even after learning that Claudius murdered his father.

Model answer:
Hamlet can be seen as a tragic hero because his hesitation becomes destructive. His desire to know the truth is understandable, but his delay allows corruption and violence to spread. Shakespeare makes the audience both sympathise with and feel frustrated by him.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Hamlet is a tragic hero because he has a weakness. He waits too long before acting.`
        ),
        worked(
          'Developing',
          `Hamlet's hesitation makes him complex. The audience understands his caution, but his delay helps lead to more deaths.`
        ),
        worked(
          'Grade 9 Ready',
          `Hamlet's tragic flaw is not simple cowardice but paralysis caused by thought, grief and moral uncertainty. Shakespeare makes his delay both intellectually understandable and dramatically catastrophic.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Iago is famous as a:',
          options: ['Complex villain', 'Romantic hero'],
          correct: 0
        },
        {
          type: 'true-false',
          question: "Shakespeare's characters are usually fully good or fully bad.",
          correct: false
        },
        {
          type: 'mcq',
          question: "Macbeth's tragic weakness is often seen as:",
          options: ['Ambition', 'Patience'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A character can change across a play.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A fool often uses:',
          options: ['Comedy to reveal truth', 'Silence to remove meaning'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Pick a Shakespeare character and explain what kind of character they are and why.`,
        sentenceStarter: '___ is a...',
        hints: [
          'Name the character type.',
          'Give one reason from the play.',
          'Explain how the audience may respond.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names a Shakespeare character',
          'Identifies a character type',
          'Uses evidence or a clear example',
          'Explains audience response'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why are flawed characters often more memorable than perfect ones?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 9 — THEME: DECEPTION AND DISGUISE
    // =========================================================
    makeTopic({
      id: 'eng-4-9',
      slug: 'theme-deception-and-disguise',
      title: 'Theme — Deception and Disguise',
      summary: 'Tricks, mistaken identity, appearance versus reality and hidden truth.',

      explainer: {
        title: 'Theme — Deception and Disguise',
        content: `
Deception and disguise appear in many Shakespeare plays.

Key forms of deception:

Disguise:
A character pretends to be someone else, often by changing clothes. In "Twelfth Night", Viola disguises herself as a young man called Cesario.

Trickery:
Characters deliberately mislead others. In "Much Ado About Nothing", friends trick Beatrice and Benedick into believing each loves the other.

Mistaken identity:
Characters fail to recognise someone or misunderstand who someone is. This is common in comedies.

Manipulation:
A character controls another person's beliefs or actions through lies. Iago does this to Othello.

Supernatural deception:
In "A Midsummer Night's Dream", fairies use magic to confuse lovers.

Appearance versus reality:
Things are not what they seem. This is one of Shakespeare's most important ideas.

Deception can create:
- Comedy, when confusion is harmless or joyful
- Tension, when the audience knows the truth but characters do not
- Tragedy, when lies destroy trust, love or life

The key question is:
How dangerous is it when people trust appearances?
`
      },

      interactiveLesson: {
        title: 'Deception',
        steps: [
          {
            type: 'question',
            question: 'In "Twelfth Night", Viola disguises herself as:',
            options: ['A young man called Cesario', 'A witch'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A harmless trick often creates:',
            options: ['Comedy', 'Tragedy'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Appearance versus reality means:',
            options: ['Things are not what they seem', 'Everything is obvious'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
In "Much Ado About Nothing", Beatrice and Benedick are tricked into believing each loves the other.

Model answer:
This is a comic use of deception. The audience understands the trick while the characters do not, creating dramatic irony. Unlike tragic deception, this trick helps the characters admit feelings they were hiding, so Shakespeare presents deception as playful and socially healing.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `The trick is funny because the audience knows more than the characters.`
        ),
        worked(
          'Developing',
          `Shakespeare uses dramatic irony because the audience watches Beatrice and Benedick misunderstand what is happening. This creates comedy.`
        ),
        worked(
          'Grade 9 Ready',
          `The deception is benevolent rather than destructive. Shakespeare uses comic manipulation to expose emotional truth, suggesting that disguise and trickery can sometimes reveal what direct speech conceals.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Mistaken identity is common in Shakespeare’s:',
          options: ['Comedies', 'Histories only'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Deception always leads to tragedy in Shakespeare.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Iago’s lies in "Othello" are an example of:',
          options: ['Manipulation', 'Harmless comedy'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Dramatic irony can happen when the audience knows the truth but a character does not.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Disguise usually involves:',
          options: ['Pretending to be someone else', 'Explaining the truth immediately'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Explain how ONE example of deception or disguise changes the direction of a Shakespeare play.`,
        sentenceStarter: 'In ___, the deception...',
        hints: [
          'Name the play.',
          'Describe the deception.',
          'Explain what changes afterwards.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names a play',
          'Describes the deception or disguise',
          'Explains the effect on the plot',
          'Comments on comedy, tension or tragedy'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does Shakespeare often let the audience know about a trick while the characters remain unaware?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 10 — THEME: LOVE
    // =========================================================
    makeTopic({
      id: 'eng-4-10',
      slug: 'theme-love',
      title: 'Theme — Love',
      summary: 'Romantic, jealous, unrequited, family, comic and tragic love.',

      explainer: {
        title: 'Theme — Love',
        content: `
Love is one of Shakespeare's most important themes, but he does not present it in only one way.

Types of love:

Romantic love:
Passionate love between two people. Romeo and Juliet are the most famous example.

Love at first sight:
Sudden attraction, often treated as exciting but also risky.

Unrequited love:
Loving someone who does not love you back. Helena in "A Midsummer Night's Dream" suffers from this.

Jealous love:
Love twisted by suspicion or possession. Othello's jealousy becomes destructive.

Enduring love:
Love that survives difficulty, honesty and testing.

Comic love:
Love complicated by disguise, misunderstanding or witty argument.

Family love:
Love between parents, children or relatives. Shakespeare often shows family love as both protective and controlling.

Contrasting love:
Shakespeare often compares different relationships. In "Much Ado About Nothing", Claudio and Hero's love is idealised and fragile, while Beatrice and Benedick's love is witty, equal and slower to develop.

Shakespeare suggests love can:
- Heal
- Confuse
- Transform
- Control
- Destroy
`
      },

      interactiveLesson: {
        title: 'Types of Love',
        steps: [
          {
            type: 'question',
            question: "Loving someone who doesn't love you back is:",
            options: ['Unrequited love', 'Enduring love'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Jealous love is strongly associated with:',
            options: ['Othello', 'Much Ado About Nothing'],
            correct: 0
          },
          {
            type: 'question',
            question: "Beatrice and Benedick's love is based on:",
            options: ['Wit and equality', 'Silent obedience'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
Romeo and Juliet fall in love within a single scene and marry the next day.

Model answer:
Shakespeare presents their love as sudden, intense and idealised. However, the speed of their relationship also creates danger because they act before thinking carefully. The audience may admire their passion while fearing its consequences.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Romeo and Juliet's love is very quick and passionate. This makes it exciting but dangerous.`
        ),
        worked(
          'Developing',
          `Their love is idealised, but Shakespeare also shows that sudden love can make characters ignore risk.`
        ),
        worked(
          'Grade 9 Ready',
          `Shakespeare makes Romeo and Juliet's love both transcendent and reckless. Its intensity challenges the hatred around them, but its speed also traps them inside a private world where secrecy replaces judgement.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Idealised, sudden love is common in:',
          options: ['Comedies and tragedies', 'Histories only'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Shakespeare only shows one kind of love.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Othello shows love damaged by:',
          options: ['Jealousy', 'Weather'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Family love in Shakespeare can sometimes feel controlling.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Beatrice and Benedick’s relationship is often based on:',
          options: ['Witty argument and equality', 'Immediate tragedy'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Compare two different kinds of love shown in Shakespeare. What does the contrast suggest?`,
        sentenceStarter: 'One kind of love is...',
        hints: [
          'Name two characters or couples.',
          'Explain how their relationships differ.',
          'Say what Shakespeare may be suggesting about love.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names two kinds of love',
          'Uses Shakespearean examples',
          'Contrasts them clearly',
          'Draws a wider conclusion'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why might a slow-burn love feel more stable than love at first sight in Shakespeare?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 11 — THEME: FATE
    // =========================================================
    makeTopic({
      id: 'eng-4-11',
      slug: 'theme-fate',
      title: 'Theme — Fate',
      summary: 'Prophecy, destiny, omens, doom and free will.',

      explainer: {
        title: 'Theme — Fate',
        content: `
Fate is the idea that events are already decided or shaped by forces beyond human control.

Shakespeare often explores the tension between fate and free will.

Fate:
Events seem inevitable or controlled by destiny, prophecy, stars or supernatural forces.

Free will:
Characters make choices and are responsible for their actions.

Key examples:

"Romeo and Juliet":
The lovers are called "star-crossed" in the prologue. This tells the audience from the start that their love is doomed.

"Macbeth":
The witches give Macbeth prophecies. The question is whether they control his future or whether Macbeth chooses to make the prophecies happen.

Important signs of fate:
- Prophecies
- Omens
- Dreams
- References to stars
- Foreshadowing
- Warnings
- Repeated images of doom

The most interesting Shakespeare answer usually avoids saying "it is only fate" or "it is only choice". Often, Shakespeare shows both working together.
`
      },

      interactiveLesson: {
        title: 'Fate vs Free Will',
        steps: [
          {
            type: 'question',
            question: 'Romeo and Juliet are described as:',
            options: ['Star-crossed lovers', 'Old friends'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Fate is the idea that events are:',
            options: ['Already decided or shaped by destiny', 'Always random and meaningless'],
            correct: 0
          },
          {
            type: 'question',
            question: 'The witches’ prophecies in "Macbeth" raise the question:',
            options: ['Does Macbeth cause what they predict?', 'Can Macbeth sing?'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
Romeo says, "I fear too early, for my mind misgives some consequence, yet hanging in the stars..."

Model answer:
Romeo's reference to "the stars" links his future to fate. The verb "misgives" suggests anxiety, as if he senses disaster before it happens. Shakespeare creates tension because the audience already knows from the prologue that the lovers are doomed.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `The word "stars" links Romeo's future to fate. He feels something bad may happen.`
        ),
        worked(
          'Developing',
          `Romeo's fear creates tension because the audience knows the play will end tragically. Shakespeare uses foreshadowing to make the future feel unavoidable.`
        ),
        worked(
          'Grade 9 Ready',
          `Romeo imagines fate as something "hanging in the stars", suggesting his life is suspended beneath forces he cannot control. Yet his choices still matter, making the tragedy feel both destined and preventable.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'A prophecy is:',
          options: ['A prediction of future events', 'A battle scene'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Fate and free will can both be important in a Shakespeare tragedy.',
          correct: true
        },
        {
          type: 'mcq',
          question: '"Star-crossed" suggests:',
          options: ['Doomed by fate', 'Very good at astronomy'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Foreshadowing can make the audience expect disaster.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'The witches in "Macbeth" mainly give Macbeth:',
          options: ['Prophecies', 'A wedding invitation'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Explain how the theme of fate adds tension to a Shakespeare play.`,
        sentenceStarter: 'The theme of fate adds tension because...',
        hints: [
          'Use an example.',
          'Mention what the audience knows.',
          'Consider whether characters still make choices.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Explains fate clearly',
          'Uses a Shakespeare example',
          'Links fate to tension',
          'Considers audience knowledge'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Does Macbeth fall because the witches predict his future, or because he chooses to act on their words?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 12 — THEME: THE SUPERNATURAL
    // =========================================================
    makeTopic({
      id: 'eng-4-12',
      slug: 'theme-the-supernatural',
      title: 'Theme — The Supernatural',
      summary: 'Witches, ghosts, fairies, spirits, fear and mystery.',

      explainer: {
        title: 'Theme — The Supernatural',
        content: `
The supernatural appears often in Shakespeare. It includes forces beyond the ordinary natural world.

Examples:

Witches:
In "Macbeth", the witches are mysterious, threatening and linked to prophecy, temptation and disorder.

Ghosts:
In "Hamlet", the ghost of Hamlet's father demands revenge and pushes the living into action.

Fairies:
In "A Midsummer Night's Dream", fairies are mischievous and magical. They cause confusion but not lasting tragedy.

Spirits:
In "The Tempest", Ariel is a spirit who serves Prospero and helps shape events.

The supernatural can create:
- Fear
- Mystery
- Comedy
- Moral confusion
- A sense of fate
- Questions about control

Context matters:
Many people in Shakespeare's time took belief in witches, ghosts and spirits seriously. A supernatural figure onstage could feel more frightening or powerful to the original audience than it may feel to some modern viewers.

Key question:
Does the supernatural control characters, or does it reveal what they already desire?
`
      },

      interactiveLesson: {
        title: 'The Supernatural',
        steps: [
          {
            type: 'question',
            question: 'Supernatural figures in "Macbeth" include:',
            options: ['Witches', 'Fairies only'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Fairies in "A Midsummer Night\'s Dream" are mainly:',
            options: ['Mischievous and magical', 'Murderous rulers'],
            correct: 0
          },
          {
            type: 'question',
            question: 'The ghost in "Hamlet" demands:',
            options: ['Action from the living', 'A comic dance'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
The witches in "Macbeth" chant, "Fair is foul, and foul is fair."

Model answer:
This line introduces moral confusion. The reversed structure suggests that good and evil will become difficult to tell apart. Because the line is spoken by witches, Shakespeare makes the supernatural feel dangerous and connected to disorder.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `"Fair is foul" shows confusion because good and bad seem mixed up.`
        ),
        worked(
          'Developing',
          `The reversed structure makes the line sound strange and memorable. It suggests the play will confuse right and wrong.`
        ),
        worked(
          'Grade 9 Ready',
          `The chiasmus compresses the play's moral disorder into a single chant. Shakespeare gives the witches language that inverts moral categories, making the supernatural appear as a force that destabilises perception itself.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Ariel in "The Tempest" serves:',
          options: ['Prospero', 'The witches'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Shakespeare always presents the supernatural as evil.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'The supernatural often creates:',
          options: ['Mystery and uncertainty', 'Only realistic detail'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Context can affect how frightening the supernatural seems.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'The ghost in "Hamlet" is connected to:',
          options: ['Revenge and duty', 'A wedding comedy'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Explain one difference between how the supernatural appears in "Macbeth" and in "A Midsummer Night's Dream".`,
        sentenceStarter: 'In "Macbeth"... whereas in "A Midsummer Night\'s Dream"...',
        hints: [
          'Think about tone.',
          'Think about fear versus comedy.',
          'Use one example from each play.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names both plays',
          'Contrasts the supernatural clearly',
          'Comments on tone or effect',
          'Uses an example'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why might Shakespeare use supernatural characters to reveal human desires rather than simply to scare the audience?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 13 — ANSWERING QUESTIONS ABOUT SHAKESPEARE
    // =========================================================
    makeTopic({
      id: 'eng-4-13',
      slug: 'answering-questions-about-shakespeare',
      title: 'Answering Questions About Shakespeare',
      summary: 'Build strong analytical answers using point, evidence, method, effect and context.',

      explainer: {
        title: 'Answering Questions About Shakespeare',
        content: `
A strong Shakespeare answer explains how Shakespeare creates meaning.

Use this structure:

1. Point
Make a clear argument about what Shakespeare presents.

Example:
Shakespeare presents Macbeth's guilt as overwhelming and inescapable.

2. Evidence
Use a short quotation.

Example:
"Will all great Neptune's ocean wash this blood clean from my hand?"

3. Method
Name or describe how the quotation works.

Example:
The hyperbole of "all great Neptune's ocean" exaggerates the scale of Macbeth's guilt.

4. Effect
Explain what the audience understands or feels.

Example:
The audience sees that Macbeth is already psychologically damaged by the murder.

5. Context
Use context only when it supports your point.

Example:
To a Jacobean audience, killing a king would seem like a crime against God's order.

Useful sentence openings:
- Shakespeare presents...
- The audience is made to feel...
- The image of...
- The stage direction suggests...
- The word "..." implies...
- This would be especially powerful to a Jacobean audience because...

Avoid:
- Retelling the plot
- Dropping in context randomly
- Using very long quotations
- Naming techniques without explaining effect
`
      },

      interactiveLesson: {
        title: 'Strong Shakespeare Answers',
        steps: [
          {
            type: 'question',
            question: 'Every analytical paragraph should contain:',
            options: ['Point, evidence and explanation', 'Only a quotation'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Context should be used:',
            options: ['Only when it supports your point', 'In every sentence'],
            correct: 0
          },
          {
            type: 'question',
            question: 'For plays, you should often comment on:',
            options: ['The audience', 'Only the reader'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does Shakespeare present Macbeth's guilt after the murder?

Text:
Macbeth says, "Will all great Neptune's ocean wash this blood clean from my hand?"

Model answer:
Shakespeare presents Macbeth's guilt as enormous and inescapable. The hyperbole of "all great Neptune's ocean" suggests that even the largest possible amount of water could not cleanse him. The noun "blood" carries both physical and moral meaning: it is the literal blood of Duncan, but it also symbolises Macbeth's guilt. A Jacobean audience would see regicide as a crime against God's order, making Macbeth's horror even more severe.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Macbeth feels guilty. The word "blood" shows he is thinking about the murder.`
        ),
        worked(
          'Developing',
          `Shakespeare uses the image of "blood" to show Macbeth's guilt. It is not just physical blood but a symbol of the crime he cannot escape.`
        ),
        worked(
          'Grade 9 Ready',
          `Shakespeare transforms "blood" from a physical stain into a moral and psychological burden. Macbeth's question reveals that he already understands his guilt as permanent, making the murder spiritually irreversible as well as politically catastrophic.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The word "blood" in Macbeth often carries:',
          options: ['Physical and moral meaning', 'No meaning'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Context should be added regardless of relevance.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'A short quotation should be:',
          options: ['Embedded into your sentence', 'Copied as a huge block every time'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'You should explain how the audience responds.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A strong point should:',
          options: ['Answer the question directly', 'Retell the whole plot'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Read this line: "Out, damned spot! Out, I say!" (Lady Macbeth).\n\nWrite a short analytical paragraph explaining what Shakespeare presents about her state of mind.`,
        sentenceStarter: 'Shakespeare presents Lady Macbeth as...',
        hints: [
          'Use a short quotation.',
          'Comment on repetition.',
          'Explain what the audience sees or hears.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Clear point',
          'Short embedded quotation',
          'Analysis of language or structure',
          'Effect on audience',
          'Context used only if relevant'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does zooming in on one word often produce stronger analysis than quoting a long passage?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 14 — WARM-UP AND WORKED PRACTICE
    // =========================================================
    makeTopic({
      id: 'eng-4-14',
      slug: 'warm-up-and-worked-practice-questions-shakespeare',
      title: 'Warm-Up and Worked Practice Questions',
      summary: 'Scaffolded Shakespeare practice before longer exam responses.',

      explainer: {
        title: 'Warm-Up and Worked Practice',
        content: `
Warm-up practice helps you build confidence before writing full responses.

Warm-up questions check whether you can:
- Translate common Shakespearean words
- Identify techniques
- Explain basic character types
- Recognise themes
- Comment on performance
- Link ideas to audience response

Worked practice questions show how a strong answer is built.

Look carefully for:
- The opening overview sentence
- Short embedded quotations
- Zooming in on individual words
- Method words such as imagery, repetition, oxymoron or rhetorical question
- Audience effect
- Relevant context

A simple paragraph structure:

Shakespeare presents... 
This is shown when...
The word/phrase "..." suggests...
This makes the audience...
This links to...

Do not rush to write a long answer. A short, precise paragraph is better than a long, unfocused one.
`
      },

      interactiveLesson: {
        title: 'Warm-Up',
        steps: [
          {
            type: 'question',
            question: '"Wherefore" means:',
            options: ['Why', 'Where'],
            correct: 0
          },
          {
            type: 'question',
            question: '"Star-crossed" lovers suggests:',
            options: ['Doomed by fate', 'Interested in astronomy'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A worked answer usually starts with:',
            options: ['A clear overview point', 'A long plot summary'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Practice Question',
        content: `
Question:
How does Shakespeare create tension at the start of "Macbeth"?

Text:
Thunder and lightning. Enter three Witches.

Model answer:
Shakespeare creates tension immediately through stagecraft. The stage direction "Thunder and lightning" creates a threatening and unnatural atmosphere before any character speaks. The entrance of the witches links the storm to the supernatural, making the audience feel that disorder and danger will control the play.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `The thunder and lightning make the start of the play feel scary.`
        ),
        worked(
          'Developing',
          `Shakespeare uses the storm to create a threatening mood. The witches then make the audience think about danger and the supernatural.`
        ),
        worked(
          'Grade 9 Ready',
          `The opening stage direction creates a world where nature is already disturbed. By introducing the witches inside this storm, Shakespeare fuses weather, evil and disorder before the human plot begins.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The thunder and lightning in "Macbeth" create a mood of:',
          options: ['Threat', 'Calm'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Stage directions can be quoted in your answer.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A warm-up question helps you:',
          options: ['Build core knowledge before longer writing', 'Avoid analysis completely'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A short paragraph can still be analytical.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'The best evidence is usually:',
          options: ['Short and precise', 'As long as possible'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Write a short paragraph analysing how a Shakespeare opening uses performance or language to hook the audience.`,
        sentenceStarter: 'Shakespeare hooks the audience by...',
        hints: [
          'Name a play.',
          'Use a short quotation or stage direction.',
          'Explain the effect.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Names a play',
          'Uses a short quotation or stage direction',
          'Explains performance or language',
          'Comments on audience effect'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why are the first thirty seconds of a Shakespeare play often so carefully crafted?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 15 — WORKED PRACTICE QUESTIONS
    // =========================================================
    makeTopic({
      id: 'eng-4-15',
      slug: 'worked-practice-questions-shakespeare',
      title: 'Worked Practice Questions',
      summary: 'Fully modelled Shakespeare responses with exam-style habits.',

      explainer: {
        title: 'Worked Practice Questions',
        content: `
Worked practice helps you see what strong Shakespeare analysis looks like.

Strong answers usually:

- Start with a clear argument
- Use short, embedded quotations
- Analyse one method at a time
- Zoom in on individual words
- Explain the audience effect
- Link to theme, character or context
- Return to the question

Example structure:

Point:
Shakespeare presents Juliet as emotionally divided.

Evidence:
This is shown when she calls Romeo a "serpent heart" with a "flowering face".

Method:
The contrast between "serpent" and "flowering" creates an oxymoronic image.

Effect:
The audience sees Juliet struggling to reconcile Romeo's beauty with his violent action.

Context/theme:
This reflects the wider conflict between love and family loyalty.

Aim for depth, not just length.
`
      },

      interactiveLesson: {
        title: 'Model Habits',
        steps: [
          {
            type: 'question',
            question: 'A strong opener is:',
            options: ['A confident overview', 'A long introduction'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Quotations should usually be:',
            options: ['Short and embedded', 'Long and copied out fully'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Top-mark paragraphs usually:',
            options: ['Analyse methods and effects', 'Only retell the story'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Model Answer',
        content: `
Question:
How does Shakespeare present Juliet's feelings when she hears Tybalt has been killed?

Text:
Juliet says, "O serpent heart, hid with a flowering face!"

Model answer:
Shakespeare presents Juliet as emotionally divided between love and horror. The image "serpent heart" suggests evil, danger and betrayal, while "flowering face" suggests beauty and innocence. This contrast shows Juliet struggling to understand how Romeo can appear so beautiful yet commit such violence. The audience feels her divided loyalty because she is trapped between love for her husband and loyalty to her family.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Juliet is confused. She loves Romeo, but she is shocked by what he has done.`
        ),
        worked(
          'Developing',
          `The contrast between "serpent" and "flowering" shows Juliet's mixed feelings. Romeo seems both dangerous and beautiful to her.`
        ),
        worked(
          'Grade 9 Ready',
          `Shakespeare compresses Juliet's divided loyalty into a violent image of contradiction. "Serpent" evokes betrayal and moral danger, while "flowering" preserves Romeo's physical beauty, making Juliet's crisis emotional, familial and moral at once.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Serpent heart, hid with a flowering face" uses:',
          options: ['Contrasting imagery', 'A stage direction'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Good answers link back to the question.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'The word "serpent" suggests:',
          options: ['Danger and betrayal', 'Calm and innocence'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A model answer should only identify a technique and stop.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Audience effect means:',
          options: ['How the audience may respond or feel', 'The number of actors on stage'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Read this line from "Macbeth": "Is this a dagger which I see before me..."\n\nWrite a short model paragraph analysing Macbeth's state of mind.`,
        sentenceStarter: 'Shakespeare presents Macbeth as...',
        hints: [
          'Use a short quotation.',
          'Comment on the question form.',
          'Think about hallucination, fear and guilt.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Clear overview',
          'Short quotation',
          'Analysis of question form or imagery',
          'Explains Macbeth’s state of mind',
          'Mentions audience effect'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does a rhetorical question often reveal more about a character than a statement does?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 16 — PRACTICE QUESTIONS
    // =========================================================
    makeTopic({
      id: 'eng-4-16',
      slug: 'practice-questions-shakespeare',
      title: 'Practice Questions',
      summary: 'Independent Shakespeare practice using exam-style paragraph planning.',

      explainer: {
        title: 'Practice Questions',
        content: `
Now it is your turn to write without a full model.

Before writing:
- Read the question twice.
- Underline the focus words.
- Read the extract for meaning.
- Read again for methods.
- Choose 2–3 short quotations.
- Plan your points before starting.

While writing:
- Start with a clear overview.
- Use short embedded quotations.
- Analyse language, structure or stagecraft.
- Zoom in on individual words.
- Explain audience effect.
- Use context only where it strengthens your argument.

After writing:
- Check every sentence answers the question.
- Check you have not retold too much plot.
- Check your quotations are accurate.
- Check you explained methods, not just named them.

Useful paragraph frame:

Shakespeare presents...
This is shown through "..."
The word/phrase "..." suggests...
This makes the audience...
This links to...
`
      },

      interactiveLesson: {
        title: 'Shakespeare Exam Habits',
        steps: [
          {
            type: 'question',
            question: 'Before writing, you should:',
            options: ['Plan 2–3 points', 'Start writing immediately'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Context should appear:',
            options: ['Only where it supports a point', 'In every line'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A confident overview comes:',
            options: ['At the start of the answer', 'Only after every quotation'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does Shakespeare present power in "Macbeth"?

Model answer:
Shakespeare presents power as both tempting and destructive. When Macbeth imagines being king — "If chance will have me king, why, chance may crown me" — the repeated idea of "chance" shows that he is not yet fully committed to murder. However, the image of the "crown" reveals that the desire for power has already entered his mind. A Jacobean audience would sense danger because Macbeth is imagining a position that should be given by God, not seized by ambition.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `Macbeth wants power, but he is unsure. The word "chance" shows he is thinking about what might happen.`
        ),
        worked(
          'Developing',
          `The word "crown" shows Macbeth is attracted to power. However, "chance" suggests he has not yet decided to act violently.`
        ),
        worked(
          'Grade 9 Ready',
          `Macbeth's language reveals ambition before action. The repeated "chance" allows him to disguise desire as passivity, but the image of the "crown" exposes the forbidden object already dominating his imagination.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The word "chance" suggests:',
          options: ['Uncertainty or fate', 'Total certainty'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'You should link every point to the question.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A good plan usually contains:',
          options: ['2–3 points with evidence', 'Only the title of the play'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Plot summary is the same as analysis.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Zooming in means:',
          options: ['Explaining the effect of a specific word or phrase', 'Writing about every scene in the play'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Read: "Double, double toil and trouble; Fire burn and cauldron bubble." (Macbeth, witches).\n\nWrite a short paragraph analysing the atmosphere Shakespeare creates.`,
        sentenceStarter: 'Shakespeare creates...',
        hints: [
          'Comment on repetition.',
          'Comment on rhythm or rhyme.',
          'Mention the audience.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Clear point',
          'Embedded quotation',
          'Comments on repetition, sound or rhythm',
          'Explains atmosphere',
          'Mentions audience effect'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does the chanting rhythm of the witches' lines still feel unsettling to many audiences?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 17 — KNOWLEDGE ORGANISER
    // =========================================================
    makeTopic({
      id: 'eng-4-17',
      slug: 'shakespeare-knowledge-organisers',
      title: 'Shakespeare — Knowledge Organisers',
      summary: 'Key knowledge summary for Section 4.',

      explainer: {
        title: 'Knowledge Organiser',
        content: `
Use this summary to revise Section 4.

Core knowledge:

Shakespeare:
- William Shakespeare lived from 1564 to 1616.
- He wrote tragedies, comedies, histories and poetry.
- His plays were written for performance.

Play types:
- Tragedy: serious, usually ending in death or catastrophe.
- Comedy: confusion, disguise, love, reunion or marriage.
- History: English kings, politics and national conflict.

Language:
- "thou" = you
- "thee" = you as object
- "thy" = your
- "wherefore" = why
- "'tis" = it is
- "hark" = listen
- "anon" = soon

Form and structure:
- Prose = ordinary speech
- Verse = patterned lines
- Iambic pentameter = usually ten syllables, unstressed/stressed rhythm
- Five-act structure = exposition, development, crisis, consequences, resolution
- Turning point = moment that changes the direction of the play

Techniques:
- Imagery
- Pun
- Foreshadowing
- Oxymoron
- Rhetorical question
- Soliloquy
- Dramatic irony
- Chiasmus
- Repetition

Performance:
- Voice
- Pause
- Gesture
- Costume
- Props
- Sound
- Lighting
- Stage directions
- Interpretation

Context:
- Social hierarchy
- Divine Right of Kings
- Belief in the supernatural
- Patriarchy
- Marriage and family duty
- Groundlings
- Male actors playing female roles

Characters:
- Romantic hero
- Tragic hero
- Villain
- Fool
- Ruler
- Strong female character
- Comic servant

Themes:
- Deception and disguise
- Appearance versus reality
- Love
- Fate and free will
- The supernatural
- Ambition
- Power
- Conflict
- Guilt

Answer writing:
- Make a clear point.
- Use short embedded quotations.
- Analyse method.
- Zoom in on words.
- Explain audience effect.
- Add context only when relevant.
- Link back to the question.

Quick paragraph formula:
Shakespeare presents...
This is shown by "..."
The word/phrase "..." suggests...
This makes the audience...
This links to...
`
      },

      interactiveLesson: {
        title: 'Recall Quiz',
        steps: [
          {
            type: 'question',
            question: 'Iambic pentameter usually has:',
            options: ['10 syllables per line', '14 syllables per line'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Groundlings were:',
            options: ['The cheapest audience members at the Globe', 'Royal advisors'],
            correct: 0
          },
          {
            type: 'question',
            question: '"Star-crossed" appears in:',
            options: ['Romeo and Juliet', 'Hamlet'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Supernatural figures in "The Tempest" include:',
            options: ['Spirits such as Ariel', 'The witches from Macbeth'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Summary',
        content: `
Text:
"Fair is foul, and foul is fair." (Witches, "Macbeth")

Model answer:
This line summarises the moral confusion of the play. The reversed structure makes good and evil appear interchangeable. Because the line is spoken by witches during a stormy opening, Shakespeare links the supernatural with disorder, making the audience feel that the world of the play is unstable from the start.
`
      },

      workedExamples: [
        worked(
          'Foundation',
          `"Fair is foul" means good and bad are mixed up. It makes the play feel confusing and dark.`
        ),
        worked(
          'Developing',
          `The repeated and reversed words suggest moral confusion. The audience feels that normal rules are being disturbed.`
        ),
        worked(
          'Grade 9 Ready',
          `The chiasmic structure makes moral inversion feel inevitable: "fair" and "foul" are not merely contrasted but exchanged. Shakespeare uses the witches' language to make disorder sound ritualistic and powerful.`
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Fair is foul, and foul is fair" shows:',
          options: ['Moral confusion', 'Peace'],
          correct: 0
        },
        {
          type: 'mcq',
          question: 'A tragic hero is:',
          options: ['An important character partly destroyed by a flaw', 'A perfect person who never changes'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Context always improves an answer, regardless of relevance.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'A soliloquy reveals:',
          options: ['Private thoughts', 'Only the weather'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Shakespeare analysis should consider the audience.',
          correct: true
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Pick three ideas from Section 4, such as iambic pentameter, tragic hero, fate, soliloquy, performance or context. Explain how each one could improve your Shakespeare writing.`,
        sentenceStarter: 'Firstly,...',
        hints: [
          'Choose three clear ideas.',
          'Explain each one.',
          'Link to audience, theme or character.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Chooses three clear Shakespeare ideas',
          'Explains each accurately',
          'Links each to analysis',
          'Mentions audience, theme or character'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Write a short paragraph analysing any Shakespeare line you know. Use one short quotation, one method, one word-level comment and one audience effect.`,
        xpReward: 50
      }
    })
  ]
};

export const getEnglishContent4TopicCount = () => englishContent4.topics.length;

export default englishContent4;